import { ChevronRightIcon } from "@imi/icons/ChevronRightIcon.js";
import { MinusIcon } from "@imi/icons/MinusIcon.js";
import { PlusIcon } from "@imi/icons/PlusIcon.js";
import { ProjectIcon } from "@imi/icons/ProjectIcon.js";
import { Button } from "@imi/ui/components/Button/Button.js";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogHeader,
} from "@imi/ui/components/Dialog/Dialog.js";
import { Heading } from "@imi/ui/components/Heading/Heading.js";
import { byShortcut } from "@imi/ui/helpers/predicate.js";
import { useKeyboard } from "@imi/ui/hooks/useKeyboard.js";
import { useToast } from "@src/shared/components/Toast/useToast";
import { downloadFile } from "@src/shared/helpers/files";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { clamp, round, tryit } from "radashi";
import {
  CSSProperties,
  startTransition,
  Suspense,
  useMemo,
  useRef,
  useState,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { OnPageLoadSuccess } from "react-pdf/dist/shared/types.js";
import { z } from "zod/v4";
import { useRouteDialogProps } from "../../hooks/useRouteDialogProps";
import CustomRenderer from "./components/CustomRenderer";
import styles from "./DocumentDialog.module.css";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

const argsSchema = z.object({
  url: z.url(),
});

const SCALE_STEP = 0.1;

type PageDimensions = {
  originalHeight: number;
  originalWidth: number;
};

function DocumentDialog() {
  const { args, ...routeDialogProps } = useRouteDialogProps({ argsSchema });
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadError, setLoadError] = useState(false);
  const [scale, setScale] = useState(1);
  const zoom = (delta: number) =>
    setScale((current) =>
      round(clamp(current + delta, SCALE_STEP * 6, null), 2),
    );
  const bodyRef = useRef<HTMLElement>(null);
  const [pageDimensions, setPageDimensions] = useState<PageDimensions>();
  const fittedRef = useRef(false);
  const fitPage = (originalWidth: number, originalHeight: number) => {
    const { current: body } = bodyRef;

    if (!body) {
      return;
    }

    const { paddingBottom, paddingLeft, paddingRight, paddingTop } =
      getComputedStyle(body);
    const width =
      body.clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight);
    const height =
      body.clientHeight - parseFloat(paddingTop) - parseFloat(paddingBottom);
    const fit = Math.min(width / originalWidth, height / originalHeight);

    setScale(round(clamp(fit, SCALE_STEP, null), 2, Math.floor));
  };
  const handlePageLoadSuccess: OnPageLoadSuccess = ({
    originalHeight,
    originalWidth,
  }) => {
    setPageDimensions({ originalHeight, originalWidth });

    if (fittedRef.current) {
      return;
    }

    fitPage(originalWidth, originalHeight);
    fittedRef.current = true;
  };
  const handleFitPage = () => {
    if (pageDimensions) {
      const { originalHeight, originalWidth } = pageDimensions;

      fitPage(originalWidth, originalHeight);
    }
  };
  const { addToast } = useToast();
  const documentLoaded = useMemo(
    () => numPages !== undefined && !loadError,
    [numPages, loadError],
  );
  const goToPage = (cb: (n: number) => number) =>
    startTransition(() => {
      setPageNumber((current) => clamp(cb(current), 1, numPages));
      bodyRef.current?.scrollTo({ top: 0 });
    });
  const { t } = useTranslation("documentation", {
    keyPrefix: "dialog",
  });
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    setDownloading(true);

    const [parseUrlError, url] = tryit((url: string) => new URL(url))(args.url);

    if (parseUrlError) {
      addToast({
        message: t("invalidUrlErrorMessage"),
        title: t("errorToastTitle"),
      });
      setDownloading(false);
      return;
    }

    const [fetchError, response] = await tryit(fetch)(url);

    if (fetchError || !response.ok) {
      addToast({
        message: t("downloadFileErrorMessage"),
        title: t("errorToastTitle"),
      });
      setDownloading(false);
      return;
    }

    const blob = await response.blob();
    const fileName = decodeURIComponent(
      url.pathname.split("/").pop() ?? "datasheet.pdf",
    );

    downloadFile(blob, fileName);
    setDownloading(false);
  };

  useKeyboard(byShortcut("ArrowRight"), () => {
    if (documentLoaded) {
      goToPage((n) => n + 1);
    }
  });

  useKeyboard(byShortcut("ArrowLeft"), () => {
    if (documentLoaded) {
      goToPage((n) => n - 1);
    }
  });

  return (
    <Dialog
      style={
        {
          "--dialog-height": "85dvh",
        } as CSSProperties
      }
      {...routeDialogProps}
    >
      <DialogCloseButton>{t("close")}</DialogCloseButton>
      <DialogHeader className={styles.header}>
        <Heading as="h3" subTitle={t("subTitle")}>
          {t("title")}
        </Heading>

        <Button
          className={styles.downloadButton}
          disabled={downloading}
          onClick={handleDownload}
        >
          {downloading ? t("downloading") : t("download")}
        </Button>
      </DialogHeader>

      <div className={styles.viewport}>
        <DialogBody className={styles.body} ref={bodyRef}>
          <ErrorBoundary fallback={<p>{t("failedToLoad")}</p>}>
            <Suspense fallback={<p>{t("loadingDocument")}</p>}>
              <Document
                file={args.url}
                onLoadError={() => setLoadError(true)}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              >
                <Suspense fallback={<p>{t("loadingPage")}</p>}>
                  <Page
                    customRenderer={CustomRenderer}
                    onLoadSuccess={handlePageLoadSuccess}
                    pageNumber={pageNumber}
                    renderMode="custom"
                    scale={scale}
                  />
                </Suspense>
              </Document>
            </Suspense>
          </ErrorBoundary>
        </DialogBody>

        {documentLoaded && (
          <div className={styles.zoom}>
            <Button
              onClick={handleFitPage}
              size="sm"
              title={t("fitToPage")}
              variant="outline"
            >
              <ProjectIcon />
            </Button>
            <Button
              onClick={() => zoom(SCALE_STEP)}
              size="sm"
              title={t("zoomIn")}
              variant="outline"
            >
              <PlusIcon />
            </Button>
            <Button
              onClick={() => zoom(-SCALE_STEP)}
              size="sm"
              title={t("zoomOut")}
              variant="outline"
            >
              <MinusIcon />
            </Button>
          </div>
        )}
      </div>

      {documentLoaded && (
        <footer className={styles.footer}>
          <nav className={styles.nav}>
            <Button
              disabled={pageNumber === 1}
              onClick={() => goToPage((n) => n - 1)}
              title={t("previousPage")}
              type="button"
            >
              <ChevronRightIcon className={styles.flipHorizontally} />
            </Button>

            <span className={styles.pages}>
              {pageNumber} / {numPages}
            </span>

            <Button
              disabled={pageNumber === numPages}
              onClick={() => goToPage((n) => n + 1)}
              title={t("nextPage")}
              type="button"
            >
              <ChevronRightIcon />
            </Button>
          </nav>
        </footer>
      )}
    </Dialog>
  );
}

export default DocumentDialog;
